import React from "react";

/**
 * Controlled ListType component.
 *
 * Props:
 *   attribute  – { type: "list", attributeName: string, data: string[] }
 *   onChange   – (updatedAttribute) => void
 *   onDelete   – () => void
 *   language   – "en" | "zh"
 */
export const ListType = ({
    attribute,
    onChange,
    onDelete,
    language = "en",
}) => {
    const items = Array.isArray(attribute.data) ? attribute.data : [];

    const updateName = (value) => {
        onChange({ ...attribute, attributeName: value });
    };

    const addItem = () => {
        onChange({ ...attribute, data: [...items, ""] });
    };

    const updateItem = (index, value) => {
        const updated = [...items];
        updated[index] = value;
        onChange({ ...attribute, data: updated });
    };

    const deleteItem = (index) => {
        onChange({ ...attribute, data: items.filter((_, i) => i !== index) });
    };

    return (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
            {/* ── Title bar ── */}
            <div className="bg-[#3C3C3C] px-4 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 shrink-0">
                        {language === "zh" ? "列表" : "List"}
                    </span>
                    <input
                        type="text"
                        value={attribute.attributeName}
                        placeholder={
                            language === "zh" ? "屬性名稱" : "Attribute name"
                        }
                        className="flex-1 bg-transparent border-0 border-b-2 border-gray-200 focus:border-white px-1 py-0.5 text-sm font-medium text-white focus:outline-none placeholder-gray-400 transition-colors"
                        onChange={(e) => updateName(e.target.value)}
                    />
                </div>
                <button
                    type="button"
                    className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors shrink-0"
                    onClick={onDelete}
                    title={language === "zh" ? "刪除屬性" : "Delete attribute"}
                >
                    <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            </div>

            {/* ── Values section ── */}
            <div className="bg-white px-4 py-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {language === "zh" ? "值" : "Values"}
                    </label>
                    <button
                        type="button"
                        className="px-2.5 py-1 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors whitespace-nowrap"
                        onClick={addItem}
                    >
                        + {language === "zh" ? "添加項目" : "Add Item"}
                    </button>
                </div>

                {items.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-3 bg-gray-50 rounded-md border border-dashed border-gray-200">
                        {language === "zh"
                            ? "尚無項目，點擊上方添加"
                            : "No items yet — click Add Item above"}
                    </p>
                ) : (
                    <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto">
                        {items.map((item, index) => (
                            <div
                                key={index}
                                className="flex flex-row gap-2 items-center group"
                            >
                                <span className="text-xs text-gray-400 w-5 text-right shrink-0 font-mono">
                                    {index + 1}.
                                </span>
                                <input
                                    type="text"
                                    value={item}
                                    placeholder={
                                        language === "zh"
                                            ? "請輸入值"
                                            : "Enter value"
                                    }
                                    className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    onChange={(e) =>
                                        updateItem(index, e.target.value)
                                    }
                                />
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        className="p-1 text-gray-300 group-hover:text-red-400 hover:!text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                        onClick={() => deleteItem(index)}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
